import { Stack, Typography, useTheme } from "@mui/material";
import { FoodsIcon, WhiteBox } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    getPostTypeColor,
    PUBLICATION_CATEGORIES,
    PublicationCategory,
    SOCIAL_POST_TYPE_VISUAL,
} from "../../utils/postType";

interface PostCategoriesProps {
    activeCategory: PublicationCategory | null;
    onCategoryClick: (category: PublicationCategory | null) => void;
    showHint?: boolean;
}

const CategoriesHeader = ({ title }: { title: string }) => (
    <Stack direction="row" alignItems="center" gap={1}>
        <FoodsIcon icon="categoryTag" size={2} />
        <Typography variant="h6">{title}</Typography>
    </Stack>
);

export const PostCategoriesStrip = ({ activeCategory, onCategoryClick }: PostCategoriesProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const chipSx = (selected: boolean, color: string) => ({
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        cursor: "pointer",
        border: `1px solid ${selected ? color : theme.custom.grey[400]}`,
        backgroundColor: selected ? `${color}14` : theme.palette.common.white,
        transition: "background-color 0.15s ease, border-color 0.15s ease",
    });

    return (
        <Stack
            direction="row"
            gap={1}
            sx={{
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                onClick={() => onCategoryClick(null)}
                sx={chipSx(activeCategory === null, theme.palette.primary.main)}
            >
                <FoodsIcon icon="forum" size={1} />
                <Typography variant="small-bold">{t("social.categories.all_short", "All")}</Typography>
            </Stack>
            {PUBLICATION_CATEGORIES.map((category) => {
                const visual = SOCIAL_POST_TYPE_VISUAL[category];
                const color = getPostTypeColor(theme, visual.colorToken);
                const selected = activeCategory === category;

                return (
                    <Stack
                        key={category}
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                        onClick={() => onCategoryClick(category)}
                        sx={chipSx(selected, color)}
                    >
                        <FoodsIcon icon={visual.icon} size={1} />
                        <Typography variant="small-bold" sx={{ color: selected ? color : undefined }}>
                            {t(visual.labelKey, visual.defaultLabel)}
                        </Typography>
                    </Stack>
                );
            })}
        </Stack>
    );
};

export const PostCategories = ({ activeCategory, onCategoryClick, showHint = true }: PostCategoriesProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const title = t("social.categories.title", "Categories");

    return (
        <WhiteBox sx={{ p: 2 }}>
            <Stack gap={2}>
                <CategoriesHeader title={title} />
                {showHint && (
                    <Typography variant="caption" color="text.secondary">
                        {t("social.categories.hint", "Filter the feed by publication type.")}
                    </Typography>
                )}
                <Stack gap={0.5}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1.5}
                        onClick={() => onCategoryClick(null)}
                        sx={{
                            cursor: "pointer",
                            p: 1,
                            borderRadius: 2,
                            backgroundColor: activeCategory === null ? theme.palette.primary[50] : "transparent",
                            border:
                                activeCategory === null ?
                                    `1px solid ${theme.palette.primary[200]}`
                                :   "1px solid transparent",
                            transition: "background-color 0.15s ease, transform 0.15s ease",
                            "&:hover": {
                                backgroundColor: theme.palette.primary[50],
                                transform: "translateX(4px)",
                            },
                        }}
                    >
                        <FoodsIcon icon="forum" size={2} backgroundColor={theme.palette.primary[100]} />
                        <Typography variant="body-bold">
                            {t("social.categories.all_publications", "All publications")}
                        </Typography>
                    </Stack>

                    {PUBLICATION_CATEGORIES.map((category) => {
                        const visual = SOCIAL_POST_TYPE_VISUAL[category];
                        const color = getPostTypeColor(theme, visual.colorToken);
                        const selected = activeCategory === category;

                        return (
                            <Stack
                                key={category}
                                direction="row"
                                alignItems="center"
                                gap={1.5}
                                onClick={() => onCategoryClick(category)}
                                sx={{
                                    cursor: "pointer",
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: selected ? `${color}14` : "transparent",
                                    border: selected ? `1px solid ${color}40` : "1px solid transparent",
                                    transition: "background-color 0.15s ease, transform 0.15s ease",
                                    "&:hover": {
                                        backgroundColor: `${color}14`,
                                        transform: "translateX(4px)",
                                    },
                                }}
                            >
                                <FoodsIcon icon={visual.icon} size={2} backgroundColor={`${color}20`} />
                                <Stack minWidth={0}>
                                    <Typography variant="body-bold" sx={{ color: selected ? color : undefined }}>
                                        {t(visual.labelKey, visual.defaultLabel)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>
            </Stack>
        </WhiteBox>
    );
};
